import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { mockData } from '../mock';
import { Award, Quote, Users, ArrowRight } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WallOfFame = () => {
  const [alumni, setAlumni] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchWallOfFame();
  }, []);

  const fetchWallOfFame = async () => {
    try {
      const response = await axios.get(`${API}/alumni-feedback/wall-of-fame`);
      setAlumni(response.data);
    } catch (error) {
      console.error('Error fetching wall of fame:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f7f4ec]">
      {/* Header */}
      <header className="bg-[#14202e] py-4 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-3">
            <img src={mockData.logo} alt="JurisGo" className="h-8 w-8" />
            <span className="text-xl font-serif text-[#f7f4ec]">JurisGo</span>
          </Link>
          <nav className="hidden md:flex items-center space-x-6">
            <Link to="/" className="text-[#f7f4ec]/70 hover:text-[#f7f4ec] text-sm">Home</Link>
            <Link to="/trainings" className="text-[#f7f4ec]/70 hover:text-[#f7f4ec] text-sm">Trainings</Link>
            <Link to="/opportunities" className="text-[#f7f4ec]/70 hover:text-[#f7f4ec] text-sm">Opportunities</Link>
            <Link to="/wall-of-fame" className="text-[#9c7a3c] text-sm font-medium">Wall of Fame</Link>
          </nav>
          <Link to="/alumni-feedback">
            <Button className="bg-[#9c7a3c] hover:bg-[#8a6b35] text-white text-sm">
              Share Your Story
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-[#14202e] py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-10 left-10 w-64 h-64 bg-[#9c7a3c] rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-80 h-80 bg-[#9c7a3c] rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-4xl mx-auto px-4 text-center relative">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#9c7a3c]/20 border border-[#9c7a3c]/30 rounded-full mb-6">
            <Award className="h-4 w-4 text-[#9c7a3c]" />
            <span className="font-mono text-xs text-[#9c7a3c] uppercase tracking-wider">Alumni Success Stories</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-serif text-[#f7f4ec] mb-4">
            Wall of Fame
          </h1>
          <p className="text-lg text-[#f7f4ec]/60 max-w-2xl mx-auto">
            Celebrating the achievements of our JurisGo alumni who have successfully embarked on their paralegal careers
          </p>
        </div>
      </section>

      {/* Stats Bar */}
      <div className="bg-[#14202e]/5 border-y border-[#14202e]/10 py-6">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-8 md:gap-16">
            <div className="text-center">
              <p className="text-3xl font-serif text-[#9c7a3c]">{alumni.length}</p>
              <p className="text-sm text-[#14202e]/60">Success Stories</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-serif text-[#9c7a3c]">5</p>
              <p className="text-sm text-[#14202e]/60">Batches Completed</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-serif text-[#9c7a3c]">100%</p>
              <p className="text-sm text-[#14202e]/60">Industry Relevant</p>
            </div>
          </div>
        </div>
      </div>

      {/* Alumni Grid */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="text-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#9c7a3c] mx-auto mb-4"></div>
              <p className="text-[#14202e]/60">Loading success stories...</p>
            </div>
          ) : alumni.length === 0 ? (
            <div className="text-center py-20">
              <Users className="h-16 w-16 text-[#14202e]/20 mx-auto mb-4" />
              <h3 className="text-xl font-serif text-[#14202e] mb-2">Be the First!</h3>
              <p className="text-[#14202e]/60 mb-6">No stories have been shared yet. Be the first to share your JurisGo journey!</p>
              <Link to="/alumni-feedback">
                <Button className="bg-[#9c7a3c] hover:bg-[#8a6b35] text-white">
                  Share Your Story <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </Link>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {alumni.map((person) => (
                <Card key={person.id} className="border-0 shadow-lg bg-white overflow-hidden group hover:shadow-xl transition-shadow">
                  <CardContent className="p-0">
                    {/* Header with photo and name */}
                    <div className="bg-[#14202e] p-6 relative">
                      <div className="absolute top-4 right-4">
                        <Quote className="h-8 w-8 text-[#9c7a3c]/30" />
                      </div>
                      <div className="flex items-center gap-4">
                        {person.headshot_url ? (
                          <img 
                            src={person.headshot_url} 
                            alt={person.full_name}
                            className="w-16 h-16 rounded-full object-cover border-2 border-[#9c7a3c]"
                          />
                        ) : (
                          <div className="w-16 h-16 rounded-full border-2 border-[#9c7a3c] bg-[#9c7a3c]/20 flex items-center justify-center flex-shrink-0">
                            <span className="text-2xl font-serif text-[#9c7a3c]">
                              {person.full_name ? person.full_name.charAt(0).toUpperCase() : 'A'}
                            </span>
                          </div>
                        )}
                        <div>
                          <h3 className="text-lg font-serif text-[#f7f4ec]">{person.full_name}</h3>
                          <Badge className="bg-[#9c7a3c]/20 text-[#9c7a3c] border-[#9c7a3c]/30 mt-1">
                            {person.batch_number}
                          </Badge>
                        </div>
                      </div>
                      {/* Show role if consented */}
                      {person.display_role_publicly && person.role_title && (
                        <div className="mt-4 pt-4 border-t border-[#f7f4ec]/10">
                          <p className="text-sm text-[#9c7a3c]">
                            Now working as: <span className="font-medium text-[#f7f4ec]">{person.role_title}</span>
                          </p>
                        </div>
                      )}
                    </div>
                    
                    {/* Feedback content */}
                    <div className="p-6">
                      <p className="text-[#14202e]/80 leading-relaxed text-sm">
                        "{person.course_feedback}"
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#14202e]">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-serif text-[#f7f4ec] mb-4">
            Are You a JurisGo Alumni?
          </h2>
          <p className="text-[#f7f4ec]/60 mb-8">
            Share your success story and inspire the next generation of paralegals. Your journey could be the motivation someone needs.
          </p>
          <Link to="/alumni-feedback">
            <Button className="bg-[#9c7a3c] hover:bg-[#8a6b35] text-white px-8 py-6 text-lg">
              Share Your Story <ArrowRight className="h-5 w-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#14202e] border-t border-[#f7f4ec]/10 py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <img src={mockData.logo} alt="JurisGo" className="h-6 w-6" />
            <span className="font-serif text-[#f7f4ec]">JurisGo</span>
          </div>
          <p className="text-sm text-[#f7f4ec]/40">
            India's Premier International Paralegal Training Platform
          </p>
          <p className="text-xs text-[#f7f4ec]/30 mt-4">
            &copy; {new Date().getFullYear()} JurisGo. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default WallOfFame;
